"use client";

import Icon from "@/components/Icon/Icon";
import { buttonVariants } from "@/components/ui/button";
import { useFormatDate } from "@/hooks/date";
import { cn } from "@/lib/cn";
import { getCurrentLocale } from "@/lib/i18n";
import { useCallback, useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import { YearMonthPicker } from "./YearMonthPicker";

type CalendarCaptionProps = {
  displayMonth: Date;
  onClick: () => void;
};

/**
 * カレンダーのキャプション（年月表示部分）コンポーネント
 */
const CalendarCaption = ({ displayMonth, onClick }: CalendarCaptionProps) => {
  const formatDate = useFormatDate();
  return (
    <div onClick={onClick} className="flex cursor-pointer items-center gap-1">
      {formatDate(displayMonth, { preset: "yearMonth" })}
      <Icon iconName="ChevronDownIcon" />
    </div>
  );
};

type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  showYearMonthSelect?: boolean; // 年月選択UIを表示するかどうか
  defaultMonth?: Date; // 初期表示月を指定するプロパティ
};

/**
 * デフォルトのスタイル設定(shadcnのデフォルト定義)
 * Tailwind CSSのクラス名を使用してカレンダーの各部分のスタイルを定義
 */
const defaultClassNames = {
  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
  month: "space-y-4",
  caption: "flex justify-center pt-1 relative items-center",
  nav: "space-x-1 flex items-center",
  nav_button: cn(
    buttonVariants({ variant: "outline" }),
    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
  ),
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  table: "w-full border-collapse space-y-1",
  head_row: "flex",
  head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
  row: "flex w-full mt-2",
  day: cn(
    buttonVariants({ variant: "ghost" }),
    "h-8 w-8 p-0 font-normal aria-selected:opacity-100",
  ),
  day_range_start: "day-range-start",
  day_range_end: "day-range-end",
  day_selected:
    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
  day_today: "bg-primary-muted-fill text-primary-on-muted-fill",
  day_outside:
    "day-outside text-muted-foreground opacity-50 aria-selected:bg-primary-muted-fill aria-selected:text-muted-foreground aria-selected:opacity-30",
  day_disabled: "text-muted-foreground opacity-50",
  day_range_middle:
    "aria-selected:bg-primary-muted-fill aria-selected:text-primary-on-muted-fill",
  day_hidden: "invisible",
};

/**
 * カレンダーコンポーネント
 * react-day-pickerをベースにカスタマイズしたカレンダー
 * 年月選択機能やカスタムスタイリングをサポート
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  showYearMonthSelect = false,
  defaultMonth,
  ...props
}: CalendarProps) {
  const locale = getCurrentLocale();
  const formatDate = useFormatDate();
  const [pickerType, setPickerType] = useState<"year" | "month" | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(
    defaultMonth || props.month || new Date(),
  );

  const handleYearSelect = useCallback(
    (year: number) => {
      const newDate = new Date(currentMonth);
      newDate.setDate(1); // 年を変更する前に1日に設定して月末の繰り上がりを防ぐ
      newDate.setFullYear(year);
      setCurrentMonth(newDate);
      props.onMonthChange?.(newDate);
    },
    [currentMonth, props.onMonthChange],
  );

  const handleMonthSelect = useCallback(
    (month: number) => {
      const newDate = new Date(currentMonth);
      newDate.setDate(1); // 月を変更する前に1日に設定して月末の繰り上がりを防ぐ
      newDate.setMonth(month);
      setCurrentMonth(newDate);
      props.onMonthChange?.(newDate);
      setPickerType(null);
    },
    [currentMonth, props.onMonthChange],
  );

  useEffect(() => {
    if (props.month) {
      setCurrentMonth(props.month);
    }
  }, [props.month]);

  const getComponents = () => {
    if (showYearMonthSelect) {
      return {
        Caption: ({ displayMonth }: { displayMonth: Date }) => (
          <CalendarCaption
            displayMonth={displayMonth}
            onClick={() => setPickerType(pickerType ? null : "year")}
          />
        ),
      };
    }
    return {
      IconLeft: () => <Icon iconName="ChevronLeft" />,
      IconRight: () => <Icon iconName="ChevronRight" />,
    };
  };

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
    props.onMonthChange?.(date);
  };

  return (
    <div className="relative">
      <DayPicker
        month={currentMonth}
        showOutsideDays={showOutsideDays}
        className={cn("p-3", className)}
        onMonthChange={handleMonthChange}
        classNames={{
          ...defaultClassNames,
          caption_label: cn(
            "text-sm font-medium inline-flex items-center gap-1 hover:bg-primary-muted-fill hover:text-on-primary-muted-fill rounded-md px-2 py-1 cursor-pointer",
            pickerType && "bg-primary-muted-fill",
          ),
          cell: cn(
            "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-primary-muted-fill [&:has([aria-selected].day-outside)]:bg-primary-muted-fill [&:has([aria-selected].day-range-end)]:rounded-r-md",
            props.mode === "range"
              ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
              : "[&:has([aria-selected])]:rounded-md",
          ),
          ...classNames,
        }}
        components={getComponents()}
        formatters={
          !showYearMonthSelect
            ? {
                formatCaption: (date) =>
                  formatDate(date, { preset: "yearMonth" }),
              }
            : undefined
        }
        locale={locale}
        {...props}
      />
      {showYearMonthSelect && pickerType === "year" && (
        <div className="absolute left-0 right-0 top-12 z-50 rounded-md border bg-popover p-2 shadow-md">
          <YearMonthPicker
            currentMonth={currentMonth}
            onYearSelect={handleYearSelect}
            onMonthSelect={handleMonthSelect}
          />
        </div>
      )}
    </div>
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
